import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PhoneIcon from '@mui/icons-material/Phone';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';

import { Fade, useScrollTrigger } from '@mui/material';
import React, { useContext, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';

import { ApiContext } from '_lib/api/apiContext';
import { SimpleAppBar } from '_lib/components/AppBar/SimpleAppBar';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Card } from '_lib/components/Card/Card';
import { PrimaryCardHeader } from '_lib/components/CardHeader/CardHeader';
import { Divider } from '_lib/components/Divider/Divider';
import { Fab } from '_lib/components/Fab/Fab';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';

import { ExternalLink } from '_lib/components/Link/Link';
import { Paper } from '_lib/components/Paper/Paper';
import { Stack } from '_lib/components/Stack/Stack';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { useMediaQuery } from '_lib/hooks/useMediaQuery';

import { formatPhone } from '_lib/utils/helpers';

import { AppDrawerState, AppRouteContext } from '../App/app.types';
import { AuthContext } from '../auth/authContext';
import { CallCenterAdmin, SystemUser } from '../auth/permissionHelpers';
import { CallSegmentListDisplay } from '../CallSegment/CallSegmentListDisplay';
import { IconTypography } from '../IconTypography/IconTypography';
import { KeyValueGrid } from '../KeyValueGrid/KeyValueGrid';
import { appRoutePaths, getFullAppRoutePath, hasGoBack } from '../Nav/nav.types';

import { AiCallDisplay } from './AiCallDisplay';

type AiCallDetailProps = {
  appDrawerState?: AppDrawerState;
  id: string;
};

export const AiCallDetail = (props: AiCallDetailProps) => {
  const { appDrawerState, id } = props;

  const navigate = useNavigate();
  const location = useLocation();

  const api = useContext(ApiContext);

  const { authUserCacheResource } = useContext(AuthContext);

  const cachedAuthUserQuery = authUserCacheResource.useCacheQuery();

  const singleColumn = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  // https://stackoverflow.com/a/56743659
  const [scrollTarget, setScrollTarget] = useState<HTMLDivElement | undefined>(undefined);
  const scrollTrigger = useScrollTrigger({ target: scrollTarget, disableHysteresis: true, threshold: 0 });

  const gridItemSize = singleColumn ? 12 : 6;

  const aiCallDetailQuery = api.aiCalls.useGetById({ id });
  const aiCallDetailData = aiCallDetailQuery.data;

  const companyQuery = api.companies.useGetById(
    aiCallDetailData ? { id: aiCallDetailData.companyId } : { skipQuery: true }
  );
  const companyData = companyQuery.data;

  const aiVoicesQuery = api.appConfig.useAiVoiceConfigQuery();

  const aiVoice = aiVoicesQuery.data?.results?.find((voice) => voice.id === aiCallDetailData?.providerCall.aiVoiceId);

  const callSegmentsQuery = api.callSegments.useUnpagedList(
    {
      params: {
        telephonyId: aiCallDetailData?.telephonyId || undefined,
        phoneCallId: aiCallDetailData?.phoneCallId || undefined
      }
    },
    { enabled: !!aiCallDetailData }
  );

  const isFetching =
    aiCallDetailQuery.isFetching ||
    companyQuery.isFetching ||
    aiVoicesQuery.isFetching ||
    cachedAuthUserQuery.isFetching ||
    callSegmentsQuery.isFetching;

  return (
    <FlexBox sx={{ flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
      <SimpleAppBar
        title='CPI Call Details'
        onBackClicked={() => {
          const locationState = location.state;
          if (hasGoBack(locationState)) {
            navigate(locationState.goBack.url);
          } else {
            navigate(getFullAppRoutePath(appRoutePaths.CallLog));
          }
        }}
        onAppDrawerToggleClicked={appDrawerState?.openSlidingDrawer}
      />
      <Divider />
      <FlexBox sx={{ overflow: 'hidden', height: '100%', width: '100%', position: 'relative' }}>
        <BackdropSpinner open={isFetching} />
        {aiCallDetailData && companyData && cachedAuthUserQuery.data && (
          <Paper
            ref={(node) => {
              if (node) {
                setScrollTarget(node);
              }
            }}
            variant='elevation'
            elevation={3}
            sx={(theme) => ({
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: theme.palette.background.default,
              borderRadius: 0,
              padding: 3,
              width: '100%',
              height: '100%',
              overflow: 'auto'
            })}>
            <Stack useFlexGap spacing={2}>
              <Card>
                <PrimaryCardHeader title='General Info' />
                <KeyValueGrid
                  gridItemSize={gridItemSize}
                  padding={2}
                  keyValueArray={[
                    {
                      label: 'Caller ID',
                      textOrComponent: (
                        <IconTypography
                          startIcon={<PhoneCallbackIcon color='primary' />}
                          text={aiCallDetailData.callerId ? formatPhone(aiCallDetailData.callerId) : 'N/A'}
                        />
                      )
                    },
                    {
                      label: 'Company DNIS',
                      textOrComponent: (
                        <IconTypography
                          startIcon={<PhoneIcon color='primary' />}
                          text={aiCallDetailData.companyDNIS ? formatPhone(aiCallDetailData.companyDNIS) : 'N/A'}
                        />
                      )
                    },
                    { label: 'Company Name', textOrComponent: companyData.name || 'Unknown Company' },
                    ...((SystemUser({ user: cachedAuthUserQuery.data }) ||
                      CallCenterAdmin({ user: cachedAuthUserQuery.data })) &&
                    aiCallDetailData.providerCall?.providerId &&
                    aiCallDetailData.providerCall?.providerType === 'vapi'
                      ? [
                          {
                            label: 'Provider Call',
                            textOrComponent: (
                              <ExternalLink
                                href={`https://dashboard.vapi.ai/calls/${aiCallDetailData.providerCall.providerId}`}>
                                Vapi Call
                              </ExternalLink>
                            )
                          }
                        ]
                      : [])
                  ]}
                />
              </Card>
              <Card>
                <PrimaryCardHeader title='CPI Call Info' />
                <AiCallDisplay
                  aiCall={aiCallDetailData}
                  aiVoice={aiVoice}
                  canEdit={
                    SystemUser({ user: cachedAuthUserQuery.data }) ||
                    CallCenterAdmin({ user: cachedAuthUserQuery.data })
                  }
                  gridItemSize={gridItemSize}
                  timezone={companyData.timezone}
                />
              </Card>
              {callSegmentsQuery.data && callSegmentsQuery.data?.length > 0 && (
                <CallSegmentListDisplay
                  currentCallSegmentId={aiCallDetailData.id}
                  callSegments={callSegmentsQuery.data}
                  companyTimezone={companyData.timezone}
                />
              )}
            </Stack>
            <Fade in={scrollTrigger}>
              <Tooltip title='Scroll to top'>
                <Fab
                  size='small'
                  sx={{ position: 'sticky', bottom: '8px', alignSelf: 'flex-end' }}
                  onClick={() => {
                    scrollTarget?.scrollTo(0, 0);
                  }}>
                  <KeyboardArrowUpIcon />
                </Fab>
              </Tooltip>
            </Fade>
          </Paper>
        )}
      </FlexBox>
    </FlexBox>
  );
};

export const AiCallDetailRoute = () => {
  const { appDrawerState } = useOutletContext<AppRouteContext>();
  const { id } = useParams() as { id: string };

  return <AiCallDetail appDrawerState={appDrawerState} id={id} />;
};
